import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { get } from "../lib/api";
import { logout } from "../lib/auth";
import { isTyping } from "../lib/hooks";
import { Button } from "../ui";
import logo from "../styles/preascent-logo.png";
import { NAV, NAV_BOTTOM } from "./nav";
import { useShell, Toasts } from "./context";
import LogTouchModal from "./LogTouchModal";
import CreateModal from "./CreateModal";
import CommandPalette from "./CommandPalette";
import LeadModal from "./LeadModal";
import SignalModal from "./SignalModal";

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");

function NavItem({ item, badge }) {
    return (
        <NavLink to={item.path} className="hz-nav-item" activeClassName="active">
            <i className={item.icon}></i>
            <span>{item.label}</span>
            {badge > 0 && <span className="hz-nav-badge" title={item.badgeTitle || "Overdue"}>{badge}</span>}
        </NavLink>
    );
}

export default function AppShell({ children }) {
    const shell = useShell();
    const location = useLocation();
    const mainRef = useRef(null);
    const [badges, setBadges] = useState({});

    // Overdue counts for the People and Companies badges.
    useEffect(() => {
        let cancelled = false;
        Promise.all([get("v1/crm/due", { kind: "contact", limit: 1 }), get("v1/crm/due", { kind: "company", limit: 1 })])
            .then(([c, co]) => !cancelled && setBadges((b) => ({ ...b, contacts: c.counts.overdue, companies: co.counts.overdue })))
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [shell.version]);

    // Leads badge: open leads that are stalled or have no next step.
    useEffect(() => {
        let cancelled = false;
        get("v1/crm/pipeline", { stalled_only: "true" })
            .then((p) => !cancelled && setBadges((b) => ({ ...b, leads: p.count })))
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [shell.version]);

    useEffect(() => {
        if (mainRef.current) mainRef.current.scrollTop = 0;
    }, [location.pathname]);

    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                shell.togglePalette();
                return;
            }
            if (e.metaKey || e.ctrlKey || e.altKey || isTyping(e)) return;
            if (shell.paletteOpen || shell.logState || shell.createState) return;
            if (document.querySelector(".hz-backdrop")) return;
            if (e.key === "l" || e.key === "L") {
                e.preventDefault();
                shell.openLog();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [shell]);

    return (
        <div className="hz-app hz-shell">
            <aside className="hz-sidebar">
                <div className="hz-brand">
                    <img src={logo} alt="Preascent" />
                </div>
                <nav className="hz-nav">
                    {NAV.map((section, i) => (
                        <React.Fragment key={i}>
                            {section.title && <div className="hz-nav-title">{section.title}</div>}
                            {section.items.map((item) => (
                                <NavItem key={item.key} item={item} badge={item.badge ? badges[item.badge] : 0} />
                            ))}
                        </React.Fragment>
                    ))}
                </nav>
                <div className="hz-nav-bottom">
                    {NAV_BOTTOM.map((item) => (
                        <NavItem key={item.key} item={item} />
                    ))}
                    <button type="button" className="hz-nav-item" onClick={() => logout()}>
                        <i className="ri-logout-box-r-line"></i>
                        <span>Sign out</span>
                    </button>
                </div>
            </aside>
            <div className="hz-main-col">
                <header className="hz-topbar">
                    <button type="button" className="hz-search-btn" onClick={shell.openPalette}>
                        <i className="ri-search-line" style={{ fontSize: 15 }}></i>
                        <span className="hz-spacer hz-ellipsis">Search people, companies, leads, signals…</span>
                        <span className="hz-kbd">{isMac ? "⌘K" : "Ctrl K"}</span>
                    </button>
                    <div className="hz-spacer"></div>
                    <Button variant="primary" icon="ri-chat-new-line" kbd="L" onClick={() => shell.openLog()}>
                        Log touchpoint
                    </Button>
                </header>
                <main className="hz-main" ref={mainRef}>
                    {children}
                </main>
            </div>
            <LogTouchModal />
            <CreateModal />
            <CommandPalette />
            <LeadModal />
            <SignalModal />
            <Toasts />
        </div>
    );
}
